import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { AllEntityTypeConfigurationConstants } from "../../../models/basics/basics.models";
import { QueriesEditor } from "../../components/queries/queries-editor";
import { activationStrategy } from "aurelia-router";
import { DataGridColumn, QueryFilter, ItemSource } from "../../../models/queries/queries.models";
import { computedFrom } from "aurelia-binding";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { Reflection } from "../../../utilities/reflection";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { I18N } from "aurelia-i18n";

@autoinject
@transient()
export class Queries {

    private _queryEditor: QueriesEditor;
    public initialQueryId: number = -1;
    public onRowSelected: (item) => string;
    public clickMode: any;
    public getParams: any;
    public addRoute: any;
    public editRoute: any;
    public hasNavbar: boolean;
    public enableDownloadExcel: boolean = true;
    public addNewText: string;
    public uri: string;
    public navBarUri: string;
    public settings: any;
    public gridSettings: any;
    public sortAlphabetically: boolean;
    public fixedColumns: DataGridColumn[] = [];
    public queryHeaderViewModel: string;
    public fixedFilters: QueryFilter[] = [];
    public pageCount: number;
    public onColumnPushed: ((column: DataGridColumn) => void) = () => { };
    public onColumnsPrepared: ((columns: DataGridColumn[]) => void) = () => { };
    public onItemSourcePrepared: (itemSource: ItemSource) => void = () => { };
    public navigationInstruction: any;
    public headerUri: string;
    public hasHeader: boolean;
    public selectionMode: 'none' | 'single' | 'multiple' = 'single'
    public title: string;

    public constructor(private element: Element,
        private userClaimsService: UserClaimsService,
        private i18N: I18N,
        private cacheClient: CacheClientService) { }

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    public dispatchEvent(nameEvent: string, data?: any) {
        if (this.element && nameEvent) {
            const customEvent: CustomEvent = new CustomEvent(nameEvent, { bubbles: true, detail: data });
            this.element.dispatchEvent(customEvent);
        }
    }

    set queryEditor(value: QueriesEditor) {
        this._queryEditor = value;
        if (value) {
            this.dispatchEvent("query-editor-available");
        }
    }

    @computedFrom(Reflection.getPropertyPath<Queries>(z => z._queryEditor))
    get queryEditor() {
        return this._queryEditor;
    }

    @computedFrom(Reflection.getPropertyPath<Queries>(x => x.queryEditor), Reflection.getPropertyPath<Queries>(x => x.queryEditor.columns), Reflection.getPropertyPath<Queries>(x => x.fixedColumns))
    get allColumns() {
        return this.queryEditor && this.queryEditor.columns && this.fixedColumns
            ? this.queryEditor.columns.concat(this.fixedColumns)
            : [];
    }

    public activate(params, navigationInstruction) {
        this.navigationInstruction = navigationInstruction;
        this.onRowSelected = navigationInstruction.onRowSelected;
        this.settings = navigationInstruction.settings;
        this.gridSettings = navigationInstruction.gridSettings;
        if (navigationInstruction.selectionMode && typeof navigationInstruction.selectionMode == "function") {
            this.selectionMode = navigationInstruction.selectionMode();
        }
        else {
            this.selectionMode = navigationInstruction.selectionMode || 'single';
        }
        this.fixedFilters = navigationInstruction.fixedFilters ? navigationInstruction.fixedFilters(params) : [];
        this.queryHeaderViewModel = navigationInstruction.queryHeaderViewModel ? navigationInstruction.queryHeaderViewModel : null;
        this.onColumnPushed = navigationInstruction.onColumnPushed ? navigationInstruction.onColumnPushed : () => { };
        this.onColumnsPrepared = navigationInstruction.onColumnsPrepared ? navigationInstruction.onColumnsPrepared : () => { };
        this.clickMode = navigationInstruction.clickMode ? navigationInstruction.clickMode : "double";
        this.getParams = navigationInstruction.getParams ? navigationInstruction.getParams : (item) => ({ id: item ? item.id : 0 });
        this.addRoute = navigationInstruction.createNewRoute ? (typeof navigationInstruction.createNewRoute == "string" ? navigationInstruction.createNewRoute : navigationInstruction.createNewRoute.name) : null;
        this.title = navigationInstruction.title;
        if (this.addRoute == null) {
            this.addRoute = navigationInstruction.addRoute ? (typeof navigationInstruction.addRoute == "string" ? navigationInstruction.addRoute : navigationInstruction.addRoute.name) : null;
        }
        this.editRoute = navigationInstruction.editRoute ? (typeof navigationInstruction.editRoute == "string" ? navigationInstruction.editRoute : navigationInstruction.editRoute.name) : null;
        this.uri = navigationInstruction.entity ? navigationInstruction.entity : navigationInstruction.route.split("/")[1];
        this.navBarUri = navigationInstruction.navbarUri || this.uri;
        this.hasNavbar = navigationInstruction.hasNavbar;
        this.sortAlphabetically = 'sortAlphabetically' in navigationInstruction ? navigationInstruction.sortAlphabetically : true;
        this.enableDownloadExcel = navigationInstruction.enableDownloadExcel != null ? navigationInstruction.enableDownloadExcel : this.enableDownloadExcel;
        const entityMeta = Enumerable.from(AllEntityTypeConfigurationConstants)
            .firstOrDefault(x => x.uri && x.uri.replace(/ /g, "").toLowerCase() == this.uri.replace(/-/g, "").toLowerCase());
        if (navigationInstruction.claimsToAdd && this.userClaimsService.hasPermissions(navigationInstruction.claimsToAdd)) {

            this.addNewText = entityMeta ? entityMeta.addNewTextOverride ? entityMeta.addNewTextOverride : `${this.i18N.tr("add-new-text")} ${entityMeta.typeSingular}` : null;
        } else {
            this.addNewText = null;
        }
        if (!this.editRoute) {
            this.editRoute = entityMeta ? entityMeta.editRoute : null;
        }
        this.hasHeader = navigationInstruction.hasHeader;
        this.headerUri = navigationInstruction.headerUri || this.uri;
        this.pageCount = navigationInstruction.pageCount ? navigationInstruction.pageCount : 50;
        this.onItemSourcePrepared = navigationInstruction.onItemSourcePrepared ? navigationInstruction.onItemSourcePrepared : () => { };

        if (navigationInstruction.fixedColumns && typeof navigationInstruction.fixedColumns == "function") {
            this.fixedColumns = navigationInstruction.fixedColumns();
        }
        else if (!navigationInstruction.fixedColumns && entityMeta && entityMeta.fixedColumns && typeof entityMeta.fixedColumns === "function") {
            this.fixedColumns = entityMeta.fixedColumns();
        }
        else {
            this.fixedColumns = navigationInstruction.fixedColumns ? navigationInstruction.fixedColumns : (entityMeta && entityMeta.fixedColumns ? entityMeta.fixedColumns : []);
        }

        this.cacheClient.get<number>('last_query_list_' + this.uri, CacheLocation.LocalStorage, false).then(x => {
            if (x) {
                this.initialQueryId = x;
            }
        }).catch();
    }

    onSavedQueryChanged($event: CustomEvent) {
        const queryId = $event.detail.queryId;
        this.cacheClient.put('last_query_list_' + this.uri, queryId);
    }
}
